/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */
import {useEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams} from "react-router-dom";
import {calculateHeight, isSmWidth} from "../../../../../Apis/functions";
import routes from "../../../../../Routes/routes";
import {FILE_MODEL} from "../../../Visitor/Visitor/Model/Model";
import {
    getPendingGateEventTrans,
    getVehicleMovementReasonByKey,
    MATERIAL_CONTROL_TABS,
    VEHICLE_MOVEMENT_DETAIL_MODEL
} from "../../Model/Model";
import {GetMaterialControlById} from "../../../../../reduxToolkit/MaterialControl/MaterialControlApi";
import {STATUS_MODEL} from "../../../../../enums/statusEnum";
import { USER_TYPE_NAME_MODEL } from "../../../../../enums/userTypeEnum";

/**
 * Reads the movement of a vehicle the detail displays.
 *
 * The movement is read once and the view only decides how it is displayed: the
 * information the requester registered on one side and what the gate reads on
 * the other.
 */
const VehicleMovementDetailsApp = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const {id} = useParams();
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission, user } = useSelector((state) => state.authenticatioauthennSlice);
    // A guard carries no permissions, its user type reads and closes the folio at the gate
    const isGuard = user?.data?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const [item, setItem] = useState(null);
    const [evidence, setEvidence] = useState([]);
    const containerCardRef = useRef(null);
    const contentCardRef = useRef(null);
    const containerTableRef = useRef(null);
    const contentTableRef = useRef(null);

    const statusId = item?.dispatchStatusId;
    // The dispatch is what closes the movement, so the badge of the detail reads
    // it with the shape every folio of material control carries
    const badgeItem = VEHICLE_MOVEMENT_DETAIL_MODEL.toSet(item);
    // The movement is described by the direction it takes, told with what the
    // vehicle does and not with the materials it carries
    const vehicleDirectionOption = VEHICLE_MOVEMENT_DETAIL_MODEL.getDirectionOption(item);
    // The reason the movement was registered with is read as a row of the card
    const reason = getVehicleMovementReasonByKey(item?.movementReason);
    // A movement reported at the gate is read with its report above the log,
    // because it has to be reviewed before the dispatch
    const hasDiscrepancies = statusId === STATUS_MODEL.DISCREPANCY;
    const discrepancyFiles = badgeItem?.discrepancyFiles || [];
    // A dispatched movement is closed, and the log it was dispatched with is
    // the one that tells it, so only the report the gate raised is read above
    // the detail
    const showBadge = hasDiscrepancies;

    // The gate is what closes the folio, so while it waits for a movement of it
    // the notice of the one it waits for is read instead of a badge, the same
    // way every folio of material control reads it
    const pendingGateTrans = getPendingGateEventTrans(statusId);

    /**
     * Adjusts the height of the cards of the detail to the screen, so each one
     * scrolls on its own body and the page is not the one that scrolls.
     *
     * What the folio carries above them — the badge that closes it or the notice
     * of what the gate still has to register — takes room, so the height is
     * measured again whenever one of them is displayed.
     */
    useEffect(() => {
        if (isSmWidth()) return;

        const bottom = showBadge || pendingGateTrans ? 80 : 70;

        calculateHeight({elementRef: containerCardRef, bottom: bottom});
        calculateHeight({elementRef: containerTableRef, bottom: bottom});
        calculateHeight({elementRef: contentCardRef, bottom: bottom});
        calculateHeight({elementRef: contentTableRef, bottom: bottom});
    }, [showBadge, pendingGateTrans]);

    useEffect(() => {
        getInfo();
    }, []);

    /**
     * Reads the movement being displayed.
     *
     * The service keeps the vehicle and the result of the gate in blocks of
     * their own, so the movement is read with the names the detail and the gate
     * log of the guard look for.
     *
     * @returns {void}
     */
    const getInfo = () => {
        dispatch(GetMaterialControlById(id)).then(({payload: {data: {data, success}}}) => {
            if (success !== true) return;
            setItem(VEHICLE_MOVEMENT_DETAIL_MODEL.fromService(data));
            setEvidence(data?.evidence?.map(e => FILE_MODEL.set(e)) || []);
        });
    };

    /**
     * Navigates back to the view the detail was opened from, and falls back to
     * the movements tab when the detail is opened straight from the url.
     *
     * @returns {void}
     */
    const goBack = () => {
        const backUrl = location?.state?.backUrl;
        navigate(backUrl?.pathname || routes.materialControl.main, {
            state: backUrl?.state || {currentTab: MATERIAL_CONTROL_TABS.VEHICLE_MOVEMENTS.INDEX}
        });
    };

    return {
        t,
        navigate,
        DEV,
        isGuard,
        permission,
        item: item ? {...item, movementTypeName: reason ? t(reason.trans) : null} : item,
        evidence,
        statusId,
        showBadge,
        badgeItem,
        hasDiscrepancies,
        discrepancyFiles,
        vehicleDirectionOption,
        pendingGateTrans,
        containerCardRef,
        contentCardRef,
        containerTableRef,
        contentTableRef,
        getInfo,
        goBack
    };
};

export default VehicleMovementDetailsApp;
